import { useEffect, useRef, type KeyboardEvent } from 'react';
import { content } from '../../content';
import { resolveCardDefinition } from '../../core/engine';
import type { CardInstance, GameState } from '../../core/types';
import { CardView } from './CardView';

interface DeckDialogProps {
  open: boolean;
  state: GameState;
  onClose: () => void;
}

function pileLabel(state: GameState, card: CardInstance): string {
  const combat = state.combat;
  if (!combat) return 'Expedition deck';
  if (combat.hand.includes(card.id)) return 'Hand';
  if (combat.drawPile.includes(card.id)) return 'Draw pile';
  if (combat.discardPile.includes(card.id)) return 'Discard pile';
  if (combat.exhaustPile.includes(card.id)) return 'Exhaust pile';
  return card.temporary ? 'Temporary card' : 'Outside combat cycle';
}

export function DeckDialog({ open, state, onClose }: DeckDialogProps) {
  const panel = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const run = state.run;

  useEffect(() => {
    if (!open) return;
    returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButton.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      returnFocus.current?.focus();
    };
  }, [open]);

  if (!open || !run) return null;

  const handleKeys = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== 'Tab' || !panel.current) return;
    const focusable = [...panel.current.querySelectorAll<HTMLElement>(
      'button, [href], [tabindex]:not([tabindex="-1"])',
    )].filter((element) => !element.hasAttribute('disabled') && !element.hasAttribute('hidden'));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const combat = state.combat;
  const permanentCards = run.deck.filter((card) => !card.temporary).length;
  const temporaryCards = run.deck.length - permanentCards;

  return (
    <div
      className="settings-backdrop deck-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="settings-dialog deck-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="deck-dialog-title"
        ref={panel}
        tabIndex={-1}
        onKeyDown={handleKeys}
      >
        <header role="presentation">
          <span>
            <small>Expedition inventory</small>
            <h2 id="deck-dialog-title">Deck &amp; piles</h2>
          </span>
          <button
            ref={closeButton}
            type="button"
            className="settings-close"
            aria-label="Close deck inspection"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <section className="deck-browser" aria-label="Expedition cards">
          <div className="deck-browser__summary">
            <p>
              <strong>{permanentCards}</strong> permanent cards
              {temporaryCards > 0 ? ` · ${temporaryCards} temporary` : ''}
            </p>
            {combat ? (
              <dl aria-label="Combat pile counts">
                <div><dt>Hand</dt><dd>{combat.hand.length}</dd></div>
                <div><dt>Draw</dt><dd>{combat.drawPile.length}</dd></div>
                <div><dt>Discard</dt><dd>{combat.discardPile.length}</dd></div>
                <div><dt>Exhaust</dt><dd>{combat.exhaustPile.length}</dd></div>
              </dl>
            ) : (
              <p>Upgrades and persistent Gear condition are shown on each card.</p>
            )}
          </div>

          <div className="deck-card-grid">
            {run.deck.map((card) => (
              <CardView
                key={card.id}
                definition={resolveCardDefinition(card, content)}
                instance={card}
                contextLabel={pileLabel(state, card)}
                displayOnly
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
